import { Component, OnInit, OnDestroy } from '@angular/core';
import { UserService } from '../../../../../services/user.service';
import { SuggestionListsService } from '../../services/suggestion-lists.service';
import { ComponentBackgroundService } from '../../../../../services/component-background.service';
import { SortTableService } from '../../../../../services/sort-table.service';
import { User } from '../../../../../models/user.model';
import { SuggestionPageResponse } from 'app/models/suggestion-page-response.model';
import { AppConstants } from '../../../../views/view-user-profile/app.constants';

import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';
import { SuggestionListQuery, defaultSuggestionListQuery } from '../../models/suggestion-list-query.model';

const REVIEW_SORT_TABLE = 'allSuggestionListSort';
const CREATION_DATE_FIELD = 'creationDate';
const DATE = 'date';
const DESCENDING = 'desc';
const SORT_NONE = 'none';
const ARIA_ASCENDING = 'ascending';
const ARIA_DESCENDING = 'descending';


const REVIEW_MODE = 'review';
const ASCENDING = 'asc';
const SORT_ASCENDING = '↑';
const SUGGESTION_INVALID_PERIOD = 'SUGGESTION_INVALID_PERIOD';


const SORT_DESCENDING = '↓';
const EMPTY_TEXT = '';
const ZERO_VALUE = 0;
const FILTER_STATUSES = ["NEW","POSTPONE","CANCELLED","APPROVE_CANCELLED","IMPLEMENTED","EXECUTING_TASKS"];
const ALL_MODE = 'all';


@Component({
  selector: 'app-all-suggestion-lists',
  templateUrl: './all-suggestion-lists.component.html',
  styleUrls: ['./all-suggestion-lists.component.css']
})
export class AllSuggestionListsComponent implements OnInit, OnDestroy {
  isLoaded = false;
  allSLList: any[] = [];
  allSLListClonedForSearching: any[] = [];
  cancel_associated_session: any = false;
  internalServerError = false;
  getGroupError = false;
  getSelfError = false;
  thisUser: any;
  user: User;
  isAdmin = false;
  isUser = false;
  isHead = false;
  
  currentPage = 0;
  pageSize = 10;
  totalPages = 0;
  totalElements = 0;
  
  isLoadingNextPage = false;
  
  sortIndicators: {[column: string]: string} = {creationDate: SORT_DESCENDING};
  controls = defaultSuggestionListQuery();
  appliedQuery = defaultSuggestionListQuery();
  filterError = EMPTY_TEXT;
  readonly statuses = FILTER_STATUSES;
  private requestId = ZERO_VALUE;
  private readonly destroy$ = new Subject<void>();

  currentDisplayMode: 'all' | 'review' = ALL_MODE;


  constructor(public suggestionListsService: SuggestionListsService,
    public componentBackgroundService: ComponentBackgroundService,
    private userService: UserService,
    public sortTableService: SortTableService,
) {

  }

  checkRole() {
    this.user = this.userService.getLocal();
    
    this.isHead = false;
    this.isAdmin = false;
    this.isUser = false;
    
    this.user.roles.forEach(role => {
      switch(role) {
        case 'ROLE_HEAD':
          this.isHead = true;
          break;
        case 'ROLE_ADMIN':
          this.isAdmin = true;
          break;
        case 'ROLE_USER':
          this.isUser = true;
          break;
      }
    });
  }

  ngOnInit() {
    this.checkRole();
    this.getList();
  }

  ngOnDestroy() {
    this.requestId++;
    this.destroy$.next();
    this.destroy$.complete();
  }

  getList(page: number = ZERO_VALUE) { this.load(ALL_MODE, page); }
  getSLForReview(page: number = ZERO_VALUE) { this.load(REVIEW_MODE, page); }

  private load(mode: 'all' | 'review', page: number) {
    const requestId = ++this.requestId;
    const query = Object.assign({}, this.appliedQuery);
    this.currentDisplayMode = mode;
    this.sortIndicators = {};
    if (mode === ALL_MODE) { this.sortIndicators[query.sort] = query.direction === ASCENDING ? SORT_ASCENDING : SORT_DESCENDING; }
    this.currentPage = page;
    this.internalServerError = this.getGroupError = this.getSelfError = false;
    this.isLoadingNextPage = true;
    this.allSLList = this.allSLListClonedForSearching = [];
    const snapshot = {requestId: requestId, mode: mode, page: page, query: query};
    this.userService.getSelf().takeUntil(this.destroy$).subscribe(
      self => this.loadSuggestions(self, snapshot), () => this.loadError(requestId, true));
  }

  private loadSuggestions(self, snapshot: {requestId: number; mode: string; page: number; query: SuggestionListQuery}) {
    if (snapshot.requestId !== this.requestId) { return; }
    if (self.status !== AppConstants.HTTP_STATUS.OK) { this.loadError(snapshot.requestId, true); return; }
    this.thisUser = self.body;
    const request = snapshot.mode === ALL_MODE
      ? this.suggestionListsService.getAllSLByUserIDWithPagination(self.body.mainGroup, snapshot.page,
        {size: this.pageSize, query: snapshot.query})
      : this.suggestionListsService.getSLForReviewByHeadIDWithPagination(self.body.id, snapshot.page, this.pageSize);
    request.takeUntil(this.destroy$).subscribe(
      response => this.listLoaded(response, snapshot), () => this.loadError(snapshot.requestId, false));
  }

  private listLoaded(response, snapshot: {requestId: number; page: number}) {
    if (snapshot.requestId !== this.requestId) { return; }
    if (response.status !== AppConstants.HTTP_STATUS.OK) { this.loadError(snapshot.requestId, false); return; }
    const result: SuggestionPageResponse<Object> = response.body;
    this.allSLList = result && result.suggestions || [];
    this.allSLListClonedForSearching = this.allSLList.slice();
    this.currentPage = result && result.currentPage !== undefined ? result.currentPage : snapshot.page;
    this.totalPages = result && result.totalPages || ZERO_VALUE;
    this.totalElements = result && result.totalElements || ZERO_VALUE;
    this.isLoaded = true;
    this.isLoadingNextPage = false;
  }

  private loadError(requestId: number, self: boolean) {
    if (requestId !== this.requestId) { return; }
    this.internalServerError = true;
    this.getSelfError = self;
    this.getGroupError = !self;
    this.isLoaded = true;
    this.isLoadingNextPage = false;
  }

  applyFilters() {
    this.filterError = this.controls.createdFrom && this.controls.createdTo
      && this.controls.createdFrom > this.controls.createdTo ? SUGGESTION_INVALID_PERIOD : EMPTY_TEXT;
    if (this.filterError) { return; }
    this.appliedQuery = Object.assign({}, this.controls, {search: (this.controls.search || EMPTY_TEXT).trim(),
      sort: this.appliedQuery.sort, direction: this.appliedQuery.direction});
    this.getList();
  }

  resetFilters() {
    this.controls = defaultSuggestionListQuery();
    this.appliedQuery = defaultSuggestionListQuery();
    this.filterError = EMPTY_TEXT;
    this.getList();
  }

  sortColumn(column: 'creationDate' | 'status' | 'department' | 'initiator') {
    if (this.currentDisplayMode === REVIEW_MODE) {
      this.sortTableService.sortList(REVIEW_SORT_TABLE, this.allSLListClonedForSearching,
        column === CREATION_DATE_FIELD ? DATE : column, true);
      return;
    }
    const direction = this.appliedQuery.sort === column && this.appliedQuery.direction === ASCENDING ? DESCENDING : ASCENDING;
    this.appliedQuery = Object.assign({}, this.appliedQuery, {sort: column, direction: direction});
    this.getList();
  }

  sortDirection(column: string): string {
    if (this.currentDisplayMode !== ALL_MODE || this.appliedQuery.sort !== column) { return SORT_NONE; }
    return this.appliedQuery.direction === ASCENDING ? ARIA_ASCENDING : ARIA_DESCENDING;
  }

  trackStatus(index: number, status: string): string { return status; }
  trackSuggestion(index: number, suggestion: {id: string}): string { return suggestion.id; }

  retry() { this.load(this.currentDisplayMode, this.currentPage); }

  openSLDetails(list) {
    window.open('/dashboard/suggestion-list-details/' + list.id, '_blank');
  }

  searchText(value) {
    if (this.currentDisplayMode === ALL_MODE) { this.controls.search = value; this.applyFilters(); return; }
    if (value) {
      this.filterByDescription(value);
    } else {
      this.resetSearch();
    }
  }

  private filterByDescription(value: string) {
    const regexp = new RegExp(value, 'i');
    this.allSLListClonedForSearching = [...this.allSLList].filter(elem => regexp.test(elem.description));
  }

  private resetSearch() {
    this.allSLListClonedForSearching = [...this.allSLList];
  }
  
  loadNextPage() {
    if (!this.isLoadingNextPage && this.hasNextPage()) {
      this.loadPageByMode(this.currentPage + 1);
    }
  }

  loadPreviousPage() {
    if (!this.isLoadingNextPage && this.currentPage > 0) {
      this.loadPageByMode(this.currentPage - 1);
    }
  }

  loadNext5Pages() {
    if (!this.isLoadingNextPage) {
      const targetPage = Math.min(this.currentPage + 5, this.totalPages - 1);
      if (targetPage > this.currentPage) {
        this.loadPageByMode(targetPage);
      }
    }
  }

  loadPrevious5Pages() {
    if (!this.isLoadingNextPage) {
      const targetPage = Math.max(this.currentPage - 5, 0);
      if (targetPage < this.currentPage) {
        this.loadPageByMode(targetPage);
      }
    }
  }

  private loadPageByMode(page: number) {
    if (this.currentDisplayMode === 'review') {
      this.getSLForReview(page);
    } else {
      this.getList(page);
    }
  }

  hasNextPage(): boolean {
    return this.currentPage < (this.totalPages - 1);
  }

  hasPreviousPage(): boolean {
    return this.currentPage > 0;
  }

  hasNext5Pages(): boolean {
    return this.currentPage + 5 < this.totalPages;
  }

  hasPrevious5Pages(): boolean {
    return this.currentPage >= 5;
  }

}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/suggestion-lists/components/all-suggestion-lists/all-suggestion-lists.component.ts